import { useState } from 'react'
import { Link, LoaderFunctionArgs, useLoaderData, useParams, useRevalidator } from 'react-router-dom'
import { Alert, ButtonAnchor, ButtonLink, Dropdown, useConfirm, useToast } from '../../components/common'
import { trackEvent } from '../../lib/config'
import { ClinicalRecordData, RecordInterview, fetchClinicalRecord, deleteInterview, toggleInterviewActive } from '../../api/records'
import { fetchPatient } from '../../api/patients'
import { PatientCompleteInfo } from '../patients/PatientShowPage'
import { InterviewTemplateView } from '../../interviews/InterviewTemplate'
import { getTemplate } from '../../interviews/templates'
import { useImpression } from '../../lib/useImpression'
import { getAccountPrompt } from '../../lib/upgradePrompt'
import styles from './ClinicalRecordPage.module.scss'

function InterviewBody({ interview, data, onDownloadRecipes }: { interview: RecordInterview; data: ClinicalRecordData; onDownloadRecipes: (interviewId: number, index: number | 'all') => void }) {
  if (interview.legacy_html) {
    return <div dangerouslySetInnerHTML={{ __html: interview.legacy_html }} />
  }
  const definition =
    interview.profession_key && interview.session_key && interview.version_key
      ? getTemplate(interview.profession_key, interview.session_key, interview.version_key)
      : null
  if (!definition) {
    return <p>No se pudo cargar la ficha</p>
  }
  return (
    <InterviewTemplateView
      definition={definition}
      answers={interview.answers}
      disabled
      recipeFieldLabels={data.profession.recipe_field_labels}
      recipeTypeLabels={data.profession.recipe_type_labels}
      recipeDefaultOption={data.profession.recipe_default_option}
      onDownloadRecipes={(index) => onDownloadRecipes(interview.id, index)}
      recipesSoftDisabled={!data.health_center.trial_features_enabled}
    />
  )
}

function SessionAccordionItem({
  interview,
  sessionNumber,
  data,
  expanded,
  onToggle,
  onDelete,
  onToggleActive,
  onDownloadRecipes,
}: {
  interview: RecordInterview
  sessionNumber: number
  data: ClinicalRecordData
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  onToggleActive: () => void
  onDownloadRecipes: (interviewId: number, index: number | 'all') => void
}) {
  const healthCenter = data.health_center
  const closed = healthCenter.account_state === 'closed'
  return (
    <div
      className={`panel col-xs-12 dl-horizontal record_container session-accordion-item${expanded ? ' expanded' : ''}`}
      style={{ padding: 20, backgroundColor: interview.active ? 'white' : '#F5F5F5' }}
      data-session-id={interview.id}
    >
      <div className="col-md-12 panel-body session-accordion-header" style={{ cursor: 'pointer' }} onClick={onToggle}>
        <div className="col-sm-6 col-xs-12">
          <div className="session-header-wrapper">
            <div className="session-title-row">
              <i className="material-icons accordion-chevron">{expanded ? 'expand_more' : 'chevron_right'}</i>
              <span className="session-number-label">Sesión N° {interview.active ? sessionNumber : 'ARCHIVADO'}</span>
              <span className="session-type-chip">{interview.human_session_name}</span>
            </div>
            <div className="session-date">
              {interview.date ? interview.date.split('-').reverse().join('/') : 'Fecha no ingresada'}
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xs-12 text-center">
          <p>{interview.professional.name}</p>
          <p>R.U.T {interview.professional.rut}</p>
          <p>{interview.profession_name}</p>
        </div>
        <div className="clinical-record-actions hide-pdf" onClick={(event) => event.stopPropagation()}>
          {!closed && interview.editable && (
            <>
              <ButtonLink
                to={`/health_centers/${healthCenter.id}/interviews/${interview.id}/edit?clinical_record_id=${interview.clinical_record_id}`}
                raised
                variant="none"
                iconAction="edit"
                title="Modificar"
              >
                <i className="material-icons">edit</i>
              </ButtonLink>
              <ButtonAnchor
                href="#"
                raised
                variant="none"
                iconAction="delete"
                title="Eliminar"
                onClick={(event) => {
                  event.preventDefault()
                  onDelete()
                }}
              >
                <i className="material-icons">delete</i>
              </ButtonAnchor>
            </>
          )}
          {!closed && (
            <ButtonAnchor
              href="#"
              raised
              iconAction={interview.active ? 'archive' : 'plain'}
              variant={interview.active ? 'none' : 'success'}
              title={interview.active ? 'Archivar' : 'Restaurar'}
              onClick={(event) => {
                event.preventDefault()
                onToggleActive()
              }}
            >
              <i className="material-icons">{interview.active ? 'archive' : 'redo'}</i>
            </ButtonAnchor>
          )}
        </div>
      </div>
      {expanded && (
        <div className="session-accordion-content" style={{ display: 'block' }}>
          <div className="interview-show">
            <InterviewBody interview={interview} data={data} onDownloadRecipes={onDownloadRecipes} />
          </div>
        </div>
      )}
    </div>
  )
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const healthCenterId = Number(params.healthCenterId)
  const showInactive = new URL(request.url).searchParams.get('show_inactive') === 'true'
  const [data, patient] = await Promise.all([
    fetchClinicalRecord(healthCenterId, params.patientId as string, params.recordId as string, showInactive),
    fetchPatient(healthCenterId, params.patientId as string),
  ])
  return { data, patient }
}

export default function ClinicalRecordPage() {
  const { data, patient } = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const params = useParams()
  const healthCenterId = Number(params.healthCenterId)
  const { revalidate } = useRevalidator()
  const confirm = useConfirm()
  const showToast = useToast()
  const [expandedId, setExpandedId] = useState<number | null>(() => data.interviews[0]?.id ?? null)

  const upgradePrompt = getAccountPrompt(data.health_center)
  useImpression('Upgrade Prompt Shown', upgradePrompt ? { prompt: upgradePrompt, surface: 'clinical_record' } : null)

  const recordId = data.clinical_record_id ?? params.recordId
  const closed = data.health_center.account_state === 'closed'
  const paymentsPath = `/health_centers/${healthCenterId}/payments/new`
  const showPath = `/health_centers/${healthCenterId}/patients/${data.patient.id}/clinical_records/${recordId}/show`

  const handleDownloadRecipes = (interviewId: number, index: number | 'all') => {
    if (!data.health_center.trial_features_enabled) {
      trackEvent('Prescription Blocked', { reason: 'trial_expired' })
      showToast('warning', 'El periodo de prueba del módulo de recetas ha terminado. Para seguir utilizándolo, actualiza tu cuenta a Premium')
      return
    }
    trackEvent('Prescription Downloaded', { scope: index === 'all' ? 'all' : 'single' }, { transport: 'sendBeacon' })
    window.location.assign(`/show_prescriptions?interview_id=${interviewId}&health_center_id=${healthCenterId}&recipe_index=${index}#toolbar=1`)
  }

  const handleDelete = async (interview: RecordInterview) => {
    const confirmed = await confirm({
      title: 'Eliminar sesión',
      message: 'Estas seguro de eliminar la sesión seleccionada? Esto es irreversible.',
    })
    if (!confirmed) {
      return
    }
    await deleteInterview(healthCenterId, interview.id, interview.clinical_record_id)
    trackEvent('Interview Deleted')
    revalidate()
  }

  const handleToggleActive = async (interview: RecordInterview) => {
    const result = await toggleInterviewActive(healthCenterId, interview.id, interview.clinical_record_id)
    trackEvent(interview.active ? 'Interview Archived' : 'Interview Restored')
    showToast('success', result.notice)
    revalidate()
  }

  const templates = data.profession.templates
  const showButtons = data.profession.key !== 'secretary' && templates.length > 0
  const newInterviewPath = (sessionKey: string) =>
    `/health_centers/${healthCenterId}/interviews/new?template_session_key=${sessionKey}&clinical_record_id=${recordId}`

  return (
    <div className={styles.root}>
      <div className="col-md-4 col-xs-12">
        <div className="col-xs-12 panel" id="stick_to_parent">
          <div className="module-wrapper panel-body col-md-12 col-sm-12 col-xs-12 view health-view" style={{ padding: 0 }}>
            <PatientCompleteInfo patient={patient} healthCenterId={healthCenterId} />
          </div>
        </div>
      </div>
      <div className="col-md-8 col-xs-12">
        <div className="col-xs-12 text-center" id="profession_templates">
          {showButtons && (
            <>
              {closed ? null : templates.length + 1 < 4 ? (
                templates.map((template) => (
                  <ButtonAnchor
                    key={template.session_key}
                    href={newInterviewPath(template.session_key)}
                    raised
                    onClick={() => trackEvent('Interview Template Selected', { template_key: template.session_key, source: 'button' }, { transport: 'sendBeacon' })}
                  >
                    <i className="fa fa-plus" /> {template.human_session_name}
                  </ButtonAnchor>
                ))
              ) : (
                <Dropdown
                  raised
                  buttonClassName="right module-excel-button"
                  label={
                    <>
                      <i className="fa fa-plus" /> Nuevo
                    </>
                  }
                >
                  {templates.map((template) => (
                    <li key={template.session_key}>
                      <a
                        href={newInterviewPath(template.session_key)}
                        onClick={() => trackEvent('Interview Template Selected', { template_key: template.session_key, source: 'dropdown' }, { transport: 'sendBeacon' })}
                      >
                        <i className="fa fa-plus" /> {template.human_session_name}
                      </a>
                    </li>
                  ))}
                </Dropdown>
              )}
              {data.interviews.length >= 1 && (
                <>
                  <ButtonAnchor
                    href={`/health_centers/${healthCenterId}/patients/${data.patient.id}/clinical_records/${recordId}/show/cr_files?patient_id=${data.patient.id}&clinical_record_id=${recordId}`}
                    variant="documents"
                    raised
                  >
                    <i className="fa fa-file" /> DOCUMENTOS
                  </ButtonAnchor>
                  <Dropdown
                    variant="download"
                    raised
                    buttonClassName="right module-excel-button"
                    label={
                      <>
                        <i className="fa fa-download" /> Descargar
                      </>
                    }
                  >
                    <li>
                      <a
                        href={`${showPath}.pdf?one_per_page=true`}
                        onClick={() => trackEvent('Clinical Record Exported', { format: 'pdf', one_per_page: true }, { transport: 'sendBeacon' })}
                      >
                        <p>PDF (1 por hoja)</p>
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${showPath}.pdf?one_per_page=false`}
                        onClick={() => trackEvent('Clinical Record Exported', { format: 'pdf', one_per_page: false }, { transport: 'sendBeacon' })}
                      >
                        <p>PDF (todas juntas)</p>
                      </a>
                    </li>
                  </Dropdown>
                </>
              )}
            </>
          )}
        </div>
        <div className="module-wrapper col-xs-12 view health-view">
          {closed && (
            <Alert kind="danger">
              Este centro médico se encuentra temporalmente desactivado por no pago, solo podrá visitar y descargar la historia clínica
              de sus pacientes. Puede regularizar sus pagos <a href={paymentsPath}>aquí</a>.
            </Alert>
          )}
          {data.health_center.account_state === 'soon' && (
            <Alert kind="warning">
              Este centro médico próximamente se desactivará por no pago. Solo podrá visitar y descargar la historia clínica de sus
              pacientes. Puede regularizar sus pagos <a href={paymentsPath}>aquí</a>.
            </Alert>
          )}
          <div className="row">
            <div className="col-xs-12" />
            {data.interviews.length >= 1 && data.profession.key !== 'secretary' ? (
              <div className="col-xs-12 no-padding-xs no-margin-xs">
                {data.interviews.map((interview, index) => (
                  <SessionAccordionItem
                    key={interview.id}
                    interview={interview}
                    sessionNumber={data.interviews.length - index}
                    data={data}
                    expanded={expandedId === interview.id}
                    onToggle={() => {
                      if (expandedId !== interview.id) {
                        trackEvent('Interview Expanded', { template_key: interview.session_key })
                      }
                      setExpandedId(expandedId === interview.id ? null : interview.id)
                    }}
                    onDelete={() => void handleDelete(interview)}
                    onToggleActive={() => void handleToggleActive(interview)}
                    onDownloadRecipes={handleDownloadRecipes}
                  />
                ))}
              </div>
            ) : (
              (!data.has_attention_permission || data.profession.key === 'secretary') && (
                <Alert kind="info" style={{ textAlign: 'center' }}>
                  Esta cuenta no tiene permisos para ver la información clínica del paciente
                </Alert>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
